import {useState, useMemo} from 'react';
import {Table, Avatar, Tag} from 'antd';
import {useQuery} from '@apollo/client';
import {GET_DABA_PRO_MEMBERS} from '../resolvers';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  UserOutlined,
  CaretUpFilled,
  CaretDownFilled,
} from '@ant-design/icons';
import moment from 'moment';
import styles from '../analytics.module.scss';
import UserLink from 'components/userLink/UserLink';
import {isApprovedKycStatus} from 'utils/kycStatus';

const FILTER_TABS = [
  {key: 'ALL', label: 'Most Recent'},
  {key: 'ACTIVE', label: 'Active'},
  {key: 'RECURRING', label: 'Recurring Customers'},
  {key: 'EXPIRED', label: 'Expired Subscription'},
];

export const DabaProMembersTable = () => {
  const [activeTab, setActiveTab] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState();
  const [sortOrder, setSortOrder] = useState();

  const getFilters = () => {
    switch (activeTab) {
      case 'ACTIVE':
        return {active: true};
      case 'RECURRING':
        return {isRecurring: true};
      case 'EXPIRED':
        return {expired: true};
      case 'ALL':
      default:
        return {};
    }
  };

  const queryFilters = useMemo(
    () => ({
      ...getFilters(),
      page: currentPage,
      limit,
      paginate: true,
      ...(sortBy && {sortBy}),
      ...(sortOrder && {sortOrder}),
    }),
    [activeTab, currentPage, limit, sortBy, sortOrder],
  );

  const {data, loading} = useQuery(GET_DABA_PRO_MEMBERS, {
    variables: {filters: queryFilters},
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const handleTableChange = pagination => {
    setCurrentPage(pagination.current);
    setLimit(pagination.pageSize);
  };

  const handleTabChange = key => {
    setActiveTab(key);
    setCurrentPage(1);
    setSortBy(undefined);
    setSortOrder(undefined);
  };

  const handleColumnSort = (column, order) => {
    setCurrentPage(1);
    setSortBy(column);
    setSortOrder(order);
  };

  const renderSortableTitle = (label, column) => (
    <div className={styles.sortableHeader}>
      <span>{label}</span>
      <span className={styles.sortArrowStack}>
        <CaretUpFilled
          className={styles.sortArrow}
          style={{
            color:
              sortBy === column && sortOrder === 'asc' ? '#7c3aed' : '#bfbfbf',
          }}
          onClick={event => {
            event.stopPropagation();
            handleColumnSort(column, 'asc');
          }}
        />
        <CaretDownFilled
          className={styles.sortArrow}
          style={{
            color:
              sortBy === column && sortOrder === 'desc' ? '#7c3aed' : '#bfbfbf',
          }}
          onClick={event => {
            event.stopPropagation();
            handleColumnSort(column, 'desc');
          }}
        />
      </span>
    </div>
  );

  const columns = [
    {
      title: 'User Name',
      key: 'user',
      render: (_, record) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
          <Avatar src={record?.user?.imageUrl} icon={<UserOutlined />} />
          <div style={{display: 'flex', flexDirection: 'column'}}>
            <span style={{fontWeight: 500, color: '#111827'}}>
              <UserLink userId={record?.user?.id}>
                {record?.user?.firstName} {record?.user?.lastName}
              </UserLink>
            </span>
            <span style={{fontSize: '12px', color: '#6b7280'}}>
              {record?.user?.email}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: 'KYC Status',
      key: 'kycStatus',
      render: (_, record) => {
        const status =
          record?.user?.investorProfile?.identityCard?.kycStatus ||
          'NOT_STARTED';
        let color = 'default';
        if (isApprovedKycStatus(status) || status === 'VERIFIED')
          color = 'green';
        if (status === 'PENDING') color = 'gold';
        if (status === 'REJECTED') color = 'red';
        return (
          <Tag color={color} style={{borderRadius: '12px'}}>
            {status.replace(/_/g, ' ')}
          </Tag>
        );
      },
    },
    {
      title: renderSortableTitle('Has Investments', 'hasInvested'),
      key: 'hasInvested',
      columnKey: 'hasInvested',
      align: 'center',
      render: (_, record) =>
        record?.hasInvested ? (
          <CheckCircleOutlined style={{color: '#52c41a', fontSize: '18px'}} />
        ) : (
          <CloseCircleOutlined style={{color: '#d9d9d9', fontSize: '18px'}} />
        ),
    },
    {
      title: renderSortableTitle('Has Portfolio', 'hasPortfolio'),
      key: 'hasPortfolio',
      columnKey: 'hasPortfolio',
      align: 'center',
      render: (_, record) =>
        record?.hasPortfolio ? (
          <CheckCircleOutlined style={{color: '#52c41a', fontSize: '18px'}} />
        ) : (
          <CloseCircleOutlined style={{color: '#d9d9d9', fontSize: '18px'}} />
        ),
    },
    {
      title: renderSortableTitle('Subscription Status', 'subscriptionStatus'),
      key: 'subscriptionStatus',
      columnKey: 'subscriptionStatus',
      render: (_, record) => {
        const status = record?.subscriptionStatus || 'UNKNOWN';
        const color =
          {
            ACTIVE: 'green',
            EXPIRED: 'red',
            CANCELLED: 'orange',
            INACTIVE: 'default',
          }[status] || 'blue';
        return (
          <Tag color={color} style={{borderRadius: '12px'}}>
            {status.replace(/_/g, ' ')}
          </Tag>
        );
      },
    },
    {
      title: renderSortableTitle('Total Subscriptions', 'totalSubscriptions'),
      key: 'totalSubscriptions',
      columnKey: 'totalSubscriptions',
      render: (_, record) => record?.totalSubscriptions ?? '-',
    },
    {
      title: 'Subscription',
      key: 'subscription',
      render: (_, record) => {
        const isExpired =
          record?.subscriptionStatus === 'EXPIRED' ||
          (record?.endDate && moment(record.endDate).isBefore(moment()));
        return (
          <div style={{display: 'flex', flexDirection: 'column'}}>
            <span
              style={{
                fontWeight: 500,
                color: isExpired ? '#ff4d4f' : '#52c41a',
              }}>
              {record?.subscriptionType
                ? {
                    ONE_YEAR: '1 year',
                    THREE_MONTHS: '3 months',
                    SIX_MONTHS: '6 months',
                  }[record.subscriptionType] ||
                  record.subscriptionType.replace(/_/g, ' ').toLowerCase()
                : isExpired
                ? 'Expired'
                : 'Active'}
            </span>
            <span style={{fontSize: '12px', color: '#6b7280'}}>
              {isExpired ? 'Expired:' : 'Expires:'}
              {record?.endDate ? moment(record.endDate).fromNow() : 'N/A'}
            </span>
          </div>
        );
      },
    },
    {
      title: renderSortableTitle('Start Date', 'startDate'),
      key: 'startDate',
      columnKey: 'startDate',
      render: (_, record) =>
        record?.startDate
          ? moment(record.startDate).format('DD, MMM YYYY')
          : '-',
    },
    {
      title: renderSortableTitle('End Date', 'endDate'),
      key: 'endDate',
      columnKey: 'endDate',
      render: (_, record) =>
        record?.endDate ? moment(record.endDate).format('DD, MMM YYYY') : '-',
    },
    {
      title: renderSortableTitle('Recurring Customer', 'isRecurring'),
      key: 'isRecurring',
      columnKey: 'isRecurring',
      align: 'center',
      render: (_, record) =>
        record?.isRecurring ? (
          <ReloadOutlined style={{color: '#1890ff', fontSize: '18px'}} />
        ) : null,
    },
  ];

  const dataSource = data?.getDabaProMembers?.data || [];
  const paginationData = data?.getDabaProMembers?.pagination || {};

  return (
    <div className={styles.membersTableContainer}>
      <div className={styles.membersHeader}>
        <h3 className={styles.sectionTitle}>Daba Pro Members</h3>
        <div className={styles.membersTabs}>
          {FILTER_TABS.map(tab => (
            <div
              key={tab.key}
              className={`${styles.tabItem} ${
                activeTab === tab.key
                  ? styles.activeTab
                  : activeTab === tab.key
                  ? 'activeTab'
                  : ''
              }`}
              onClick={() => handleTabChange(tab.key)}>
              {tab.label}
            </div>
          ))}
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        rowKey={record => record?.user?.id || Math.random()}
        pagination={{
          current: currentPage,
          pageSize: limit,
          total: paginationData.total || 0,
          showSizeChanger: true,
          position: ['bottomRight'],
        }}
        onChange={handleTableChange}
        className={styles.membersTable}
      />
    </div>
  );
};
